import { Table, Tooltip, Icon } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerAvatarPlus } from '../../components/custom_components/AvatarPlus'
import PerButtonCircle from '../../components/custom_components/ButtonCircle'
import { CareerPathPopover } from '../../components/custom_components/CarrerPathPopover/CareerPathPopover'
import { PerEmpty } from '../../components/custom_components/Empty'
import PerIcon from '../../components/custom_components/Icon'
import PerText from '../../components/custom_components/Text'
import { NameDisplayPreference } from '../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../shared/nameDisplayPreferencesUtil'
import { useAmountAndCurrency } from '../../shared/useAmountAndCurrency'
import { useDateTime } from '../../shared/useDateTime'
import { UserInfo, RoleName, SkillEmployeeStatusEnum, SkillEmployeeStatusDto } from '../../types'
import { filterCareerPathReferencesByLevel } from '../appraisal/contract/careerPath/careerpath-util'
import { getSortOrderByEmployeeLevel } from '../companySettings/employees/employeesLevelSorterUtil'
import { isEmployeePartner } from '../profile/contract/utils/contractDataUtil'
import { useEmployeeStore } from './EmployeeStore'
import { MeetingInviteContainer } from './MeetingInviteContainer'
import shallow from 'zustand/shallow'
import { useUserManagersRolesByCompany } from '../../graphql/company/hooks/useUserManagerRolesByCompany'
import { SubordinateEmployeeTableData } from './EmployeesView'
import { SkillStatusIcon } from '../skillStatus/SkillStatusLabelComponent'
import PerFlexRow from '../../components/custom_components/FlexRow'

type EmployeeListProps = {
  listData: SubordinateEmployeeTableData[]
  hasResultScore: boolean
  user: UserInfo
  filterResultLength: number | undefined
  hasCareerPath: boolean
  viewMonetary?: boolean
  showOfficeColumn: boolean
  updateEmployeeSkillStatus: (employeeId: string, skillStatusData?: SkillEmployeeStatusDto) => void
}
export const EmployeeList: FC<EmployeeListProps> = ({
  listData,
  hasResultScore,
  user,
  filterResultLength,
  hasCareerPath,
  viewMonetary = false,
  showOfficeColumn,
  updateEmployeeSkillStatus,
}) => {
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { formatDateShort, formatDateShortWithTimezone } = useDateTime(user)
  const [t] = useTranslation()
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null)

  const [employeeLevels, selected, companiesAndBu] = useEmployeeStore(
    (state) => [state.employeeLevels, state.selected, state.companiesAndBu],
    shallow,
  )

  const isReviewerSelected = !!companiesAndBu.filter((cbu) => cbu.companyId === selected.companyId && cbu.companyName === 'REVIEWER').length
  const { data } = useUserManagersRolesByCompany(selected.companyId)
  const userRoles: RoleName[] = data && data.findUserManagerRolesByCompany ? data.findUserManagerRolesByCompany : []
  const isCompanyManager = userRoles.includes(RoleName.CompanyManager)
  const isBuManager = userRoles.includes(RoleName.BuManager)
  const showActionsColumn = isBuManager || isCompanyManager || isReviewerSelected

  const toColumnWidth = (value: number) => `${Number(value.toFixed(1))}%`
  let employeeWidth = showOfficeColumn ? 13 : 17
  let skillStatusWidth = showOfficeColumn ? 8.8 : 8.7
  let buWidth = showOfficeColumn ? 7.5 : 8
  let officeWidth = 8.1
  let startDateWidth = showOfficeColumn ? 8.4 : 9
  let endDateWidth = showOfficeColumn ? 8.2 : 9

  if (!showActionsColumn) {
    employeeWidth += 5.3
  }
  if (!hasCareerPath) {
    employeeWidth += 2
    skillStatusWidth += 1
    buWidth += 1
    startDateWidth += 1
    endDateWidth += 1
    if (showOfficeColumn) {
      officeWidth += 1
    } else {
      employeeWidth += 1
    }
  }
  if (!hasResultScore) {
    employeeWidth += 4
    skillStatusWidth += 1
    buWidth += 1
    startDateWidth += 1
    endDateWidth += 1
    if (showOfficeColumn) {
      officeWidth += 1
    } else {
      employeeWidth += 1
    }
  }

  const compareMoneyValues = (
    employeeA: SubordinateEmployeeTableData,
    employeeB: SubordinateEmployeeTableData,
    moneyType: 'targetBonus' | 'salary',
  ) => {
    // null or undefined values should be lesser than 0 in the total order
    // null / undefined < 0 => null or undefined values should be assigned -1 or less
    const moneyValueA = employeeA !== null && employeeA[moneyType] !== null ? employeeA[moneyType]!.amount : -1
    const moneyValueB = employeeB !== null && employeeB[moneyType] !== null ? employeeB[moneyType]!.amount : -1

    return moneyValueA - moneyValueB
  }

  const sortDates = (
    employeeA: SubordinateEmployeeTableData,
    employeeB: SubordinateEmployeeTableData,
    dateType: 'contractValidTo' | 'replyEntryDate',
  ) => {
    const A = employeeA[dateType] ? employeeA[dateType] : ''
    const B = employeeB[dateType] ? employeeB[dateType] : ''
    return A.localeCompare(B)
  }

  const toggleMonetary = (text: string) =>
    viewMonetary ? (
      <PerText fontSize="xs">{text}</PerText>
    ) : (
      <PerIcon name="viewNo_stroke" color="secondary" variant="decoration" size={20} />
    )

  const employeeTableHeader = <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('employeeList.table.employee')}</Tooltip>

  const columns = [
    {
      key: 'employee',
      width: toColumnWidth(employeeWidth),
      title: employeeTableHeader,
      sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData) => {
        const nameA = computeFullname(user.nameDisplayPreference, employeeA.firstName, employeeA.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, employeeB.firstName, employeeB.lastName)
        return nameA.localeCompare(nameB)
      },
      render: (tableData: SubordinateEmployeeTableData) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={tableData.id}
          firstName={tableData.firstName}
          lastName={tableData.lastName}
          infoText={tableData.companyName}
          linkToProfile={
            tableData.id !== user.employeeId
              ? {
                  resigned: false,
                }
              : undefined
          }
          tooltip={tableData.id !== user.employeeId ? t('employeeList.table.viewProfile') : undefined}
        />
      ),
    },
    {
      key: 'actions',
      className: 'textAlign-left',
      width: '5.3%',
      render: (tableData: SubordinateEmployeeTableData) => {
        return (
          <PerButtonCircle
            data-e2e-employee-list-meeting-invite-btn={`${tableData.firstName}_${tableData.lastName}`}
            onClick={(e) => {
              e.preventDefault() // We should refactor button to not emit native event!
              setSelectedEmployeeId(tableData.id)
            }}
            color="primary"
            icon="invite_fill"
            variant="pastel"
            tooltip={t('meetingInvite.headline')}
          />
        )
      },
    },
    {
      key: 'skillStatus',
      className: 'textAlign-center',
      width: toColumnWidth(skillStatusWidth),
      title: t('employeeList.table.skillStatus'),
      sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData) => {
        const statusA = employeeA.skillStatus ? employeeA.skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown
        const statusB = employeeB.skillStatus ? employeeB.skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown
        return statusA.localeCompare(statusB)
      },
      render: (rowData: SubordinateEmployeeTableData) => {
        const skillStatus = rowData.skillStatus
        return (
          <PerFlexRow align="middle" justify="center">
            <SkillStatusIcon skillStatus={skillStatus} tooltipVisible />
            {!skillStatus && (
              <PerButtonCircle
                ml="xxs"
                color="secondary"
                variant="pastel"
                className="disable-shadow"
                tooltip={t('profile.skillCenter.public.unknownIconTooltip')}
                size="xsmall"
                customIconComponent={<Icon type="info" style={{ fontSize: 12 }} />}
              />
            )}
          </PerFlexRow>
        )
      },
    },
    {
      key: 'level',
      title: t('employeeList.table.level'),
      width: showOfficeColumn ? '6.5%' : '7%',
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
      sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData) => {
        const levelA = getSortOrderByEmployeeLevel(employeeA.contractLevel, employeeLevels)
        const levelB = getSortOrderByEmployeeLevel(employeeB.contractLevel, employeeLevels)
        return levelA - levelB
      },
      render: (tableData: SubordinateEmployeeTableData) => tableData.contractLevel,
    },
    {
      key: 'bu',
      title: t('employeeList.table.bu'),
      className: 'direction-column',
      width: toColumnWidth(buWidth),
      sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData) => {
        return employeeA.businessUnit.name.localeCompare(employeeB.businessUnit.name)
      },
      render: (tableData: SubordinateEmployeeTableData) => tableData.businessUnit.name,
    },
    ...(showOfficeColumn
      ? [
          {
            key: 'office',
            title: t('employeeList.table.office'),
            width: toColumnWidth(officeWidth),
            sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData) => {
              const officeA = employeeA.branchOfficeCode ?? ''
              const officeB = employeeB.branchOfficeCode ?? ''
              return officeA.localeCompare(officeB)
            },
            render: (tableData: SubordinateEmployeeTableData) => (
              <PerText fontSize="xs">{tableData.branchOfficeCode ?? t('employeeList.table.officeNotAvailable')}</PerText>
            ),
          },
        ]
      : []),
    {
      key: 'salary',
      title: t('employeeList.table.salary'),
      className: 'textAlign-right whiteSpace--nowrap',
      width: showOfficeColumn ? '9.1%' : '10%',
      sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData) =>
        compareMoneyValues(employeeA, employeeB, 'salary'),
      render: (tableData: SubordinateEmployeeTableData) => {
        const salaryVisible: boolean = tableData.contractLevel ? !isEmployeePartner(tableData.contractLevel) : false
        return salaryVisible && tableData.salary && tableData.salary.amount ? (
          <div style={{ float: 'right' }}>
            {toggleMonetary(formatCurrency(tableData.salary.amount, tableData.currencyForPayments, true))}
          </div>
        ) : (
          '-'
        )
      },
    },
    {
      key: 'targetBonus',
      title: t('employeeList.table.targetBonus'),
      className: 'textAlign-right whiteSpace--nowrap',
      width: showOfficeColumn ? '9.1%' : '10%',
      sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData) =>
        compareMoneyValues(employeeA, employeeB, 'targetBonus'),
      render: (tableData: SubordinateEmployeeTableData) => {
        const bonusVisible: boolean = tableData.contractLevel ? !isEmployeePartner(tableData.contractLevel) : false
        return bonusVisible ? (
          <>
            {toggleMonetary(
              tableData.targetBonus ? formatCurrency(tableData.targetBonus.amount, tableData.currencyForPayments, true) : '-',
            )}
          </>
        ) : (
          '-'
        )
      },
    },
    {
      key: 'careerPath',
      title: t('employeeList.table.careerPath'),
      className: 'textAlign-center',
      width: '7%',
      sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData) => {
        const employeeACareerPath = employeeA.careerPathEvaluation ? employeeA.careerPathEvaluation.summary : ''
        const employeeBCareerPath = employeeB.careerPathEvaluation ? employeeB.careerPathEvaluation.summary : ''
        return employeeACareerPath.localeCompare(employeeBCareerPath)
      },
      render: (tableData: SubordinateEmployeeTableData) => {
        const careerPath = tableData.careerPathEvaluation
        const references = filterCareerPathReferencesByLevel(tableData.careerPath || [], tableData.contractLevel)

        return (
          !!careerPath &&
          !!references && (
            <CareerPathPopover
              careerPath={careerPath}
              bonusReference={references.bonus}
              salaryReference={references.salary}
              currency={tableData.currencyForPayments}
            />
          )
        )
      },
    },
    {
      key: 'replyEntryDate',
      title: t('employeeList.table.replyEntryDate'),
      width: toColumnWidth(startDateWidth),
      sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData) =>
        sortDates(employeeA, employeeB, 'replyEntryDate'),
      render: (tableData: SubordinateEmployeeTableData) => (
        <PerText color="secondary" fontSize="xs">
          {formatDateShort(tableData.replyEntryDate, true)}
        </PerText>
      ),
    },
    {
      key: 'contractValidTo',
      title: t('employeeList.table.contractValidTo'),
      width: toColumnWidth(endDateWidth),
      sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData) =>
        sortDates(employeeA, employeeB, 'contractValidTo'),
      render: (tableData: SubordinateEmployeeTableData) => (
        <>
          {tableData.contractValidTo && (
            <PerText color="secondary" fontSize="xs">
              {formatDateShortWithTimezone(tableData.contractValidTo, tableData.timezone)}
            </PerText>
          )}
        </>
      ),
    },
    {
      key: 'evaluationScore',
      title: t('employeeList.table.evaluationScore'),
      width: '9%',
      sorter: (employeeA: SubordinateEmployeeTableData, employeeB: SubordinateEmployeeTableData, sortDirection: SortOrder | undefined) => {
        const resultScoreA = employeeA.resultScore ? employeeA.resultScore : 0
        const resultScoreB = employeeB.resultScore ? employeeB.resultScore : 0
        const comparaison = resultScoreA - resultScoreB
        if (comparaison === 0) {
          const fullNameA = computeFullname(user.nameDisplayPreference, employeeA.firstName, employeeA.lastName)
          const fullNameB = computeFullname(user.nameDisplayPreference, employeeB.firstName, employeeB.lastName)
          return sortDirection === 'ascend' ? fullNameA.localeCompare(fullNameB) : fullNameB.localeCompare(fullNameA)
        }
        return comparaison
      },
      render: (tableData: SubordinateEmployeeTableData) => <>{tableData.resultScore ? tableData.resultScore.toFixed(2) : '-'}</>,
    },
  ]

  let filteredColumns = columns
  if (!hasResultScore) {
    filteredColumns = filteredColumns.filter((columnElement) => columnElement.key !== 'evaluationScore')
  }
  if (!hasCareerPath) {
    filteredColumns = filteredColumns.filter((columnElement) => columnElement.key !== 'careerPath')
  }
  if (!showActionsColumn) {
    filteredColumns = filteredColumns.filter((columnElement) => columnElement.key !== 'actions')
  }

  return (
    <>
      <Table
        className="light small viewWithTabsAndInfoBox scroll-compensation"
        dataSource={listData.map((o) => ({ ...o, key: o.id }))}
        columns={filteredColumns}
        rowClassName={() => 'minHeight-row'}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <PerEmpty
              icon="employeesNo_stroke"
              heading={
                filterResultLength !== undefined && filterResultLength === 0
                  ? t('employeeList.table.noResult')
                  : t('employeeList.table.noEmployees')
              }
            />
          ),
        }}
      />
      {selectedEmployeeId && (
        <MeetingInviteContainer
          employeeId={selectedEmployeeId}
          closeDialog={() => {
            setSelectedEmployeeId(null)
          }}
          updateEmployeeSkillStatus={(skillStatusData?: SkillEmployeeStatusDto) =>
            updateEmployeeSkillStatus(selectedEmployeeId, skillStatusData)
          }
        />
      )}
    </>
  )
}
